import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShoppingBag, Shield, TrendingUp, Award, Users, Clock, DollarSign, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { useScrollAnimation, useCounterAnimation } from '../../hooks/useScrollAnimation';

const StatCard = ({ icon: Icon, value, label, delay }) => {
  const [ref, count] = useCounterAnimation(parseInt(value), 2000);
  
  return (
    <div ref={ref} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all" style={{ animationDelay: `${delay}ms` }}>
      <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-primary" size={32} />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">{count}+</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

export const AmazonBrandRegistry = ({ onOpenPopup }) => {
  const [statsRef, statsVisible] = useScrollAnimation(0.2);
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.2);

  const faqs = [
    {
      id: 1,
      question: "What is Amazon Brand Registry?",
      answer: "Amazon Brand Registry is a program that helps brand owners protect their intellectual property and create an accurate, trusted experience for customers. It provides tools for brand protection, enhanced content, and advanced advertising features."
    },
    {
      id: 2,
      question: "Do I need a trademark to enroll?",
      answer: "Yes, you need an active registered trademark with the USPTO (or other eligible trademark office). Pending applications are not eligible for enrollment."
    },
    {
      id: 3,
      question: "How long does enrollment take?",
      answer: "Once you have a registered trademark, enrollment typically takes 1-2 business days after submitting your application, though it can vary."
    },
    {
      id: 4,
      question: "What are the main benefits?",
      answer: "Benefits include proactive brand protection tools, enhanced content features like A+ Content and Stores, access to Sponsored Brands advertising, better analytics, and powerful search and reporting tools."
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Amazon Brand Registry Services | Protect Your Amazon Listings</title>
        <meta name="description" content="Professional Amazon Brand Registry enrollment services. Protect your Amazon listings from counterfeiters and unauthorized sellers with comprehensive brand protection tools." />
        <meta name="keywords" content="Amazon Brand Registry, Amazon seller protection, brand registry enrollment, Amazon listing protection, counterfeit protection, Amazon brand tools" />
      </Helmet>

      {/* Creative Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float">
            <ShoppingBag className="w-20 h-20 text-white/10" />
          </div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <Shield className="w-24 h-24 text-white/10" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">🛒 E-commerce Protection</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-in">Amazon Brand Registry</h1>
            <p className="text-xl mb-8 text-white/95 leading-relaxed max-w-3xl mx-auto animate-slide-in">
              Protect your Amazon listings and enhance your brand presence with comprehensive Brand Registry enrollment
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in">
              <Button
                onClick={onOpenPopup}
                className="bg-white text-primary hover:bg-gray-100 h-12 px-8 text-base hover:scale-105 transition-all"
              >
                Get Started Today
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base"
                onClick={() => document.getElementById('benefits').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Overview with Image */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.unsplash.com/photo-1556740749-887f6717d7e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw1fHxlY29tbWVyY2V8ZW58MHx8fHwxNzYyNTQ4OTg5fDA&ixlib=rb-4.1.0&q=85?w=800&h=600&fit=crop" 
                      alt="E-commerce business" 
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 to-transparent flex items-end p-8">
                      <div className="text-white">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                            <ShoppingBag size={32} />
                          </div>
                          <div>
                            <div className="text-2xl font-bold">Amazon Protection</div>
                            <div className="text-white/90">Marketplace Security</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  📚 Learn More
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">What is Amazon Brand Registry?</h2>
                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    Amazon Brand Registry is a program designed to help brand owners protect their intellectual property and create an accurate, trusted experience for customers on Amazon. It provides powerful tools to help you manage and grow your brand on the world's largest online marketplace.
                  </p>
                  <p>
                    Enrollment requires an active registered trademark from the USPTO (or other eligible trademark office). Once enrolled, you gain access to enhanced brand protection tools, listing control, and exclusive marketing features that can significantly boost your Amazon sales.
                  </p>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-orange-600">
                    <p className="font-semibold text-gray-900 mb-2">💡 Did You Know?</p>
                    <p className="text-gray-700">
                      Brands enrolled in Amazon Brand Registry report an average of 150% increase in sales and significantly reduced counterfeit issues.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits - Creative Design */}
      <section id="benefits" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Benefits of Brand Registry</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Powerful tools and features to protect and grow your Amazon business
            </p>
          </div>
          <div 
            ref={benefitsRef}
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto stagger-children ${benefitsVisible ? 'visible' : ''}`}
          >
            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">1</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proactive Brand Protection</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Powerful tools to detect and report potential intellectual property violations, protecting your brand from counterfeiters and unauthorized sellers with automated monitoring systems.
              </p>
            </div>

            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">2</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Award className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Enhanced Content Control</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Greater control over your product listings with the ability to add rich A+ Content, videos, comparison charts, and enhanced brand content to showcase your products professionally.
              </p>
            </div>

            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">3</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Increased Sales Potential</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Access to Sponsored Brands ads, custom Amazon Stores, and premium advertising tools that can dramatically increase your product visibility and drive more sales.
              </p>
            </div>

            <div className="group relative bg-gradient-to-br from-orange-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="absolute top-6 right-6 bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold">4</div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <ShoppingBag className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Better Analytics & Insights</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Detailed insights into customer search behavior, sales metrics, brand analytics, and market trends to inform your business strategy and optimize performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Enrollment Requirements</h2>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">To enroll in Amazon Brand Registry, you need:</h3>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="bg-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Active Registered Trademark</h4>
                    <p className="text-gray-600">
                      A registered trademark with the USPTO that appears on your products or packaging. Pending applications are not eligible.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Ability to Verify Brand Relationship</h4>
                    <p className="text-gray-600">
                      You must be able to prove your association with the brand through verification documents and trademark ownership.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Product Images with Branding</h4>
                    <p className="text-gray-600">
                      Clear images showing your trademark on your products or packaging as part of the application process.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">How We Help</h2>
            <p className="text-lg text-gray-700 mb-12">
              We provide end-to-end assistance with Amazon Brand Registry enrollment
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg border border-orange-100">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Trademark Registration</h3>
                <p className="text-gray-600 text-base">If you don't have a registered trademark, we handle the entire registration process from search to approval</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg border border-orange-100">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Application Preparation</h3>
                <p className="text-gray-600 text-base">We prepare all required documentation and information for your Brand Registry application</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg border border-orange-100">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Enrollment Support</h3>
                <p className="text-gray-600 text-base">Step-by-step guidance through the Amazon enrollment process with expert assistance</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 shadow-lg border border-orange-100">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Ongoing Management</h3>
                <p className="text-gray-600 text-base">Continued support for brand protection and marketplace enforcement strategies</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Plans Section */}
      <PricingSection plans={amazonBrandPricing} />


      {/* FAQs - Creative Design */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ❓ Got Questions?
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about Amazon Brand Registry
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gradient-to-r from-gray-50 to-white rounded-2xl px-6 border-none shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-primary py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Protect Your Amazon Brand Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Get started with trademark registration and Amazon Brand Registry enrollment
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-orange-600 hover:bg-gray-100 h-14 px-10 text-lg hover:scale-105 transition-all shadow-xl"
          >
            Start Your Enrollment Today
          </Button>
        </div>
      </section>
    </div>
  );
};